import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, X } from 'lucide-react';
import { musicService } from '../services/musicService';

const Home = ({ onPlaySong }) => {
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoAlbum, setPromoAlbum] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [playlistsData, albumsData, artistsData] = await Promise.all([
        musicService.getPlaylists(),
        musicService.getAlbums(),
        musicService.getArtists()
      ]);
      
      // Ordenar álbumes por fecha de creación (más recientes primero)
      const sortedAlbums = albumsData.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setPlaylists(playlistsData);
      setAlbums(sortedAlbums);
      setArtists(artistsData);

      // Buscar el álbum "Amor Eterno" para promoción
      const amorEternoAlbum = sortedAlbums.find(album => 
        album.title === "Amor Eterno" && album.artist_name === "Cantico Celestial"
      );
      
      if (amorEternoAlbum) {
        setPromoAlbum(amorEternoAlbum);
        // Mostrar modal siempre si el usuario NO está logueado
        const isUserLoggedIn = localStorage.getItem('auth_token');
        if (!isUserLoggedIn) {
          setTimeout(() => setShowPromoModal(true), 1000);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const closePromoModal = () => {
    setShowPromoModal(false);
  };

  const handlePlayAlbum = (album) => {
    navigate(`/album/${album.id}`);
  };

  const handlePlayPlaylist = (playlist) => {
    navigate(`/playlist/${playlist.id}`);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#00CCFF] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Promotional Modal */}
      {showPromoModal && promoAlbum && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-gradient-to-br from-white via-blue-50 to-cyan-50 rounded-2xl max-w-lg w-full shadow-2xl border-2 border-[#00CCFF] relative overflow-hidden animate-in zoom-in-95 duration-300">
            {/* Animated Watermark Background */}
            <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#00CCFF] text-8xl font-black whitespace-nowrap animate-pulse" style={{animationDuration: '3s'}}>
                NUEVO LANZAMIENTO
              </div>
              <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#056A84] text-6xl font-black rotate-12 animate-pulse" style={{animationDuration: '4s', animationDelay: '0.5s'}}>
                {promoAlbum.artist_name}
              </div>
              <div className="absolute bottom-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#00CCFF] text-7xl font-black -rotate-12 animate-pulse" style={{animationDuration: '3.5s', animationDelay: '1s'}}>
                {promoAlbum.year}
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={closePromoModal}
              className="absolute top-3 right-3 z-50 w-8 h-8 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-xl"
            >
              <X size={20} className="text-gray-700" />
            </button>

            {/* Content */}
            <div className="p-6 md:p-8 text-center relative z-10">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-[#00CCFF] to-[#056A84] text-white font-bold rounded-full mb-4 shadow-lg">
                <span className="text-xs md:text-sm">NUEVO LANZAMIENTO</span>
              </div>

              {/* Album Cover */}
              <div className="mb-4 relative">
                <div className="w-40 h-40 md:w-48 md:h-48 mx-auto rounded-xl overflow-hidden shadow-2xl ring-2 ring-[#00CCFF] ring-offset-2 transform hover:scale-105 transition-transform duration-300">
                  <img
                    src={promoAlbum.cover}
                    alt={promoAlbum.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2 bg-gradient-to-r from-[#00CCFF] to-[#056A84] bg-clip-text text-transparent">
                {promoAlbum.title}
              </h2>

              {/* Artist */}
              <p className="text-lg md:text-xl font-bold text-gray-700 mb-4">
                {promoAlbum.artist_name}
              </p>

              {/* Description */}
              <p className="text-gray-600 mb-6 text-sm md:text-base max-w-sm mx-auto">
                Descubre las nuevas alabanzas que elevarán tu espíritu y acercarán tu corazón a Dios. 
                <span className="font-bold text-[#056A84]"> {promoAlbum.tracks} canciones</span> llenas de adoración.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => {
                    closePromoModal();
                    handlePlayAlbum(promoAlbum);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-[#00CCFF] to-[#056A84] text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl flex items-center justify-center gap-2"
                >
                  <Play size={20} fill="white" />
                  <span className="text-base">Escuchar Ahora</span>
                </button>
                <button
                  onClick={closePromoModal}
                  className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 hover:scale-105 text-base"
                >
                  Más Tarde
                </button>
              </div>

              {/* Year Badge */}
              <div className="mt-4 inline-block px-3 py-1 bg-gray-100 rounded-full">
                <span className="text-xs font-bold text-gray-600">Año {promoAlbum.year}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#00CCFF] via-[#00CCFF]/90 to-[#056A84] pt-6 md:pt-20 pb-4 md:pb-12 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-2xl md:text-6xl font-black text-white drop-shadow-lg mb-2 md:mb-4">Centro de Alabanzas</h1>
          <p className="text-sm md:text-2xl text-white/90 font-medium">Tu aliado en el Ministerio de Cristo</p>
        </div>
      </div>

      <div className="px-4 md:px-8 py-6 md:py-8">
        {/* New Releases - AHORA PRIMERO */}
        <section className="mb-8 md:mb-12">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-3xl font-black text-gray-900">Nuevos Lanzamientos</h2>
            <button className="text-xs md:text-sm font-semibold text-[#00CCFF] hover:text-[#056A84] transition-colors duration-200">
              Ver todo
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-6">
            {albums.slice(0, 5).map((album) => (
              <div
                key={album.id}
                onClick={() => handlePlayAlbum(album)}
                className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl transition-all duration-300 cursor-pointer group shadow-md hover:shadow-2xl transform active:scale-95 md:hover:-translate-y-2"
              >
                <div className="relative mb-3 md:mb-4 overflow-hidden rounded-lg md:rounded-xl">
                  <img
                    src={album.cover}
                    alt={album.title}
                    className="w-full aspect-square object-cover transform transition-transform duration-500 group-hover:scale-110"
                  />
                  <button className="absolute bottom-2 right-2 md:bottom-3 md:right-3 w-10 h-10 md:w-14 md:h-14 bg-gradient-to-r from-[#00CCFF] to-[#056A84] rounded-full flex items-center justify-center opacity-0 md:group-hover:opacity-100 transform translate-y-4 md:group-hover:translate-y-0 transition-all duration-300 shadow-2xl hover:scale-110">
                    <Play size={18} className="md:w-6 md:h-6 text-white ml-0.5" fill="white" />
                  </button>
                </div>
                <h3 className="text-gray-900 font-bold mb-1 truncate text-sm md:text-base">{album.title}</h3>
                <p className="text-xs md:text-sm text-gray-500 truncate">{album.artist_name}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Playlists - AHORA SEGUNDO */}
        <section className="mb-8 md:mb-12">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-3xl font-black text-gray-900">Playlists Destacadas</h2>
            <button className="text-xs md:text-sm font-semibold text-[#00CCFF] hover:text-[#056A84] transition-colors duration-200">
              Ver todo
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {playlists.map((playlist) => (
              <div
                key={playlist.id}
                onClick={() => handlePlayPlaylist(playlist)}
                className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl transition-all duration-300 cursor-pointer group shadow-md hover:shadow-2xl transform active:scale-95 md:hover:-translate-y-2"
              >
                <div className="relative mb-3 md:mb-4 overflow-hidden rounded-lg md:rounded-xl">
                  <img
                    src={playlist.cover}
                    alt={playlist.name}
                    className="w-full aspect-square object-cover transform transition-transform duration-500 group-hover:scale-110"
                  />
                  <button className="absolute bottom-2 right-2 md:bottom-3 md:right-3 w-10 h-10 md:w-14 md:h-14 bg-gradient-to-r from-[#00CCFF] to-[#056A84] rounded-full flex items-center justify-center opacity-0 md:group-hover:opacity-100 transform translate-y-4 md:group-hover:translate-y-0 transition-all duration-300 shadow-2xl hover:scale-110">
                    <Play size={18} className="md:w-6 md:h-6 text-white ml-0.5" fill="white" />
                  </button>
                </div>
                <h3 className="text-gray-900 font-bold mb-1 md:mb-2 truncate text-sm md:text-lg">{playlist.name}</h3>
                <p className="text-xs md:text-sm text-gray-500 line-clamp-2">{playlist.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Popular Artists */}
        <section>
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-3xl font-black text-gray-900">Artistas Populares</h2>
            <button className="text-xs md:text-sm font-semibold text-[#00CCFF] hover:text-[#056A84] transition-colors duration-200">
              Ver todo
            </button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-6">
            {artists.map((artist) => (
              <div
                key={artist.id}
                onClick={() => navigate(`/artist/${artist.id}`)}
                className="bg-white p-3 md:p-5 rounded-xl md:rounded-2xl transition-all duration-300 cursor-pointer group text-center shadow-md hover:shadow-2xl transform active:scale-95 md:hover:-translate-y-2"
              >
                <div className="relative mb-2 md:mb-4 overflow-hidden rounded-full">
                  <img
                    src={artist.image}
                    alt={artist.name}
                    className="w-full aspect-square object-cover transform transition-transform duration-500 group-hover:scale-110"
                  />
                  <button className="absolute bottom-1 right-1 md:bottom-2 md:right-2 w-8 h-8 md:w-12 md:h-12 bg-gradient-to-r from-[#00CCFF] to-[#056A84] rounded-full flex items-center justify-center opacity-0 md:group-hover:opacity-100 transform translate-y-4 md:group-hover:translate-y-0 transition-all duration-300 shadow-2xl hover:scale-110">
                    <Play size={14} className="md:w-5 md:h-5 text-white ml-0.5" fill="white" />
                  </button>
                </div>
                <h3 className="text-gray-900 font-bold mb-1 truncate text-xs md:text-base">{artist.name}</h3>
                <p className="text-[10px] md:text-xs text-gray-500 truncate">{artist.genre}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
